import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_CONST } from '../const';

const PermissionContext = createContext({
  permissions: [],
  menuPermissionMap: {},
  isParentAdmin: false,
  profileRole: null,
  loading: true,
  permissionsLoaded: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  canAccessMenu: () => false,
  refreshPermissions: () => {},
});

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [menuPermissionMap, setMenuPermissionMap] = useState({});
  const [isParentAdmin, setIsParentAdmin] = useState(false);
  const [profileRole, setProfileRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      setPermissionsLoaded(false);
      return;
    }

    try {
      const response = await fetch(API_CONST.GET_MY_PERMISSIONS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPermissions(data.permissions || []);
        setMenuPermissionMap(data.menuPermissionMap || {});
        setIsParentAdmin(data.isParentAdmin || false);
        setProfileRole(data.profileRole || null);
        setPermissionsLoaded(true);
      } else {
        console.error('Error fetching permissions: HTTP', response.status);
        setPermissions([]);
        setMenuPermissionMap({});
        setIsParentAdmin(false);
        setProfileRole(null);
        setPermissionsLoaded(false);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions([]);
      setMenuPermissionMap({});
      setIsParentAdmin(false);
      setProfileRole(null);
      setPermissionsLoaded(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPermission = useCallback((permissionCode) => {
    if (isParentAdmin) return true;
    return permissions.includes(permissionCode);
  }, [permissions, isParentAdmin]);

  const hasAnyPermission = useCallback((permissionCodes) => {
    if (isParentAdmin) return true;
    return permissionCodes.some(code => permissions.includes(code));
  }, [permissions, isParentAdmin]);

  const hasAllPermissions = useCallback((permissionCodes) => {
    if (isParentAdmin) return true;
    return permissionCodes.every(code => permissions.includes(code));
  }, [permissions, isParentAdmin]);

  const canAccessMenu = useCallback((menuKey) => {
    if (isParentAdmin) return true;
    if (!permissionsLoaded) return false;
    const requiredPermission = menuPermissionMap[menuKey];
    if (!requiredPermission) return true;
    return permissions.includes(requiredPermission);
  }, [permissions, menuPermissionMap, isParentAdmin, permissionsLoaded]);

  const value = {
    permissions,
    menuPermissionMap,
    isParentAdmin,
    profileRole,
    loading,
    permissionsLoaded,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessMenu,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionContext;
